import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { useKeycloak } from "@react-keycloak/web";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import { useActiveClient } from "../context/active-client-context";

type UserInfoContextType = {
  isPublicUser: boolean;
};

export const UserInfoContext = createContext<UserInfoContextType>({
  isPublicUser: true,
});

type Props = {
  children: ReactNode;
};

export default function GraphqlProvider(props: Props): ReactElement {
  const { children } = props;

  const { keycloak } = useKeycloak();
  const { activeClientId } = useActiveClient();
  const activeClientIdRef = useRef(activeClientId);
  activeClientIdRef.current = activeClientId;
  const keycloakRef = useRef(keycloak);
  keycloakRef.current = keycloak;

  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject>>();
  const [isPublicUser, setIsPublicUser] = useState<boolean>(true);

  useEffect(() => {
    if (keycloak.authenticated) {
      const httpLink = createUploadLink({
        uri: process.env.REACT_APP_BACKEND_API_URL,
      });

      const authLink = setContext((_, { headers }) => {
        const additionalHeaders: Record<string, string> = {
          authorization: `Bearer ${keycloakRef.current.token}`,
          "Apollo-Require-Preflight": "true",
        };
        if (activeClientIdRef.current) {
          additionalHeaders["metaphore-active-client-id"] =
            activeClientIdRef.current;
        }
        return { headers: { ...headers, ...additionalHeaders } };
      });

      const graphQlClient = new ApolloClient({
        cache: new InMemoryCache(),
        link: authLink.concat(httpLink),
      });

      setIsPublicUser(false);
      setClient(graphQlClient);

      const tokenRefreshInterval = setInterval(() => {
        keycloakRef.current.updateToken(60).catch(() => {
          // eslint-disable-next-line no-console
          console.warn("Token refresh failed. Logging out...");
          void keycloakRef.current.logout();
        });
      }, 30000);

      return () => clearInterval(tokenRefreshInterval);
    }

    return undefined;
  }, [keycloak.authenticated]);

  // Re-run active queries when the user switches the active client
  const prevActiveClientRef = useRef(activeClientId);
  useEffect(() => {
    const prev = prevActiveClientRef.current;
    prevActiveClientRef.current = activeClientId;

    if (!client) return;
    if (!activeClientId || prev === activeClientId) return;

    void client.resetStore().catch(() => undefined);
  }, [activeClientId, client]);

  return (
    <UserInfoContext.Provider value={{ isPublicUser }}>
      {!client ? (
        children
      ) : (
        <ApolloProvider client={client}>{children}</ApolloProvider>
      )}
    </UserInfoContext.Provider>
  );
}
